import { selectIsPremium } from "domain/auth/auth.selectors";
import {
  ActivitiesPageFilters,
  DurationFilter,
} from "domain/entities/activities.entities";
import { SetStateAction, Dispatch, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "ui/hook/store";
import { Snackbar } from "ui/pages/snackbar";
import { useWindowWidth } from "ui/utils/hooks";
import { MobileActivitiesFilters } from "ui/pages/app/components/activities/MobileActivitiesFilters";
import { PageHeader } from "ui/pages/app/components/PageHeader";
import { translate } from "ui/i18n";
import { DesktopActivitiesFilters } from "ui/pages/app/components/activities/DesktopActivitiesFilters";
import { INSearchField } from "ui/pages/app/components/inputs/INSearchField";
import { HiAdjustments } from "react-icons/hi";
import { getFiltersCount } from "ui/utils/get-filters-count";
import { INSkeleton } from "ui/pages/app/components/inputs/INSkeleton";
import { VisualSupport } from "domain/entities/visual-supports.entities";
import { VisualSupportCard } from "ui/pages/app/components/activities/visual-supports/VisualSupportCard";
import {
  getVisualSupports,
  setIsVisualSupportLoading,
} from "domain/visual-supports/visual-supports.actions";
import {
  selectVisualSupports,
  selectVisualSupportsTotalCount,
} from "domain/visual-supports/visual-supports.selectors";

export function VisualsSupports({
  filters,
  setFilters,
}: {
  filters: ActivitiesPageFilters & {
    [key: string]: string | string[] | boolean | DurationFilter | number;
  };
  setFilters: Dispatch<
    SetStateAction<
      ActivitiesPageFilters & {
        [key: string]: string | string[] | boolean | number | DurationFilter;
      }
    >
  >;
}) {
  const dispatch = useAppDispatch();
  const isPremium = useAppSelector(selectIsPremium);
  const supports = useAppSelector(selectVisualSupports);
  const totalCount = useAppSelector(selectVisualSupportsTotalCount);
  const navigate = useNavigate();
  const isDesktop = useWindowWidth();
  const [search, setSearch] = useState("");
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const fetchSupports = async () => {
    setIsLoading(true);
    await dispatch(
      getVisualSupports({
        search,
        ...filters,
      })
    );
    setIsLoading(false);
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchSupports();
    }, 500);
    return () => clearTimeout(timeout);
  }, [filters, search]); //eslint-disable-line

  const handleCardClick = async (support: VisualSupport) => {
    if (!isPremium && !!support.premium) {
      await Snackbar.removeSubscriptionAlert();
      Snackbar.showSubscriptionAlert(
        () => navigate("/account"),
        "visual-support"
      );
    } else {
      await dispatch(setIsVisualSupportLoading(true));
      navigate(`/visual-supports?id=${support?.id}`);
    }
  };

  const resetFilters = () => {
    setFilters({
      supportObjectives: [],
    });
  };

  return (
    <div
      className={`flex flex-row overflow-x-hidden ${
        isFiltersOpen ? "filters-open" : "filters-closed"
      }`}
      style={{ width: isDesktop ? "100%" : "200vw" }}
    >
      <div className="w-screen px-2 pt-2 md:px-12 md:pt-8">
        <PageHeader
          search={search}
          setSearch={setSearch}
          title={translate("ns1:Routes.VisualSupports")}
          placeholder="VisualSupportName"
          subTitle={translate("ns1:VisualSupports.Subtitle")}
          activityCount={totalCount}
        />

        {isDesktop ? (
          <DesktopActivitiesFilters
            filters={filters}
            setFilters={setFilters}
            fetchActivities={fetchSupports}
            activitiesType="visual-supports"
            resetFilters={resetFilters}
          />
        ) : (
          <div className="mt-7 flex">
            <INSearchField
              placeholder={translate(
                "ns1:Input.Placeholders.VisualSupportName"
              )}
              value={search}
              setValue={setSearch}
              width="100%"
              height="48px"
            />
            <div className="ms-3 h-12 w-12">
              <button
                className="relative flex h-12  w-12 items-center justify-center rounded-full border border-darkGrey-200 bg-white py-2 text-base font-bold"
                onClick={() => setIsFiltersOpen(true)}
              >
                <HiAdjustments color="#2B3674" size="24" />
                <span
                  className="absolute flex h-4 w-4 items-center justify-center rounded-full bg-pink-400 text-[9px] text-white"
                  style={{ top: "-2px", right: "-2px" }}
                >
                  {getFiltersCount(filters)}
                </span>
              </button>
            </div>
          </div>
        )}

        <div className="mb-8 mt-4 grid w-full grid-cols-2 gap-2 md:mt-8 md:gap-6 lg:grid-cols-3 xl:grid-cols-4">
          {!isLoading ? (
            <>
              {supports.map((support, index) => (
                <VisualSupportCard
                  support={support}
                  key={"support" + index}
                  onClick={() => handleCardClick(support)}
                />
              ))}
            </>
          ) : (
            <>
              {[...Array(12)].map((_, index) => (
                <INSkeleton key={"support-skeleton" + index} />
              ))}
            </>
          )}
        </div>
      </div>
      {!isDesktop && (
        <MobileActivitiesFilters
          filters={filters}
          setFilters={setFilters}
          fetchActivities={fetchSupports}
          activitiesType="visual-supports"
          setIsFiltersOpen={setIsFiltersOpen}
          resetFilters={resetFilters}
        />
      )}
    </div>
  );
}
